import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    Container,
    Row,
    Col,
    Button,
    Spinner,
    Alert,
    ProgressBar,
} from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import {
    Link,
    useParams,
    useNavigate,
    useSearchParams,
} from 'react-router-dom';

import { useEcosystem } from '@/shared/contexts/EcosystemContext';
import {
    connectToAgent,
    disconnectFromAgent,
    sendMessageToAgent,
    getAgentStats,
    getAgentErrorType,
    isConnectedToAgent,
    getCurrentThreadId,
    getConversationFromAgent,
    createNewAgentConversation,
    switchAgentConversation,
    WS_ERROR_TYPES,
} from '@/shared/services/agentChatService';

import useConversationHistory from './hooks/useConversationHistory';
import ChatMessages from './components/ChatMessages';
import ChatInput from './components/ChatInput';
import ConversationHistory from './components/ConversationHistory';
import './Assistant.css';

// Persona configuration. Built per-render against the active ecosystem so
// greetings, suggested questions, and "about" copy reflect the current tenant
// (or "this ecosystem" / "RILE Connect" fallbacks for the platform-mode case).
// Persona KEYS are stable for URL routing (/assistant/maya, etc.).
const PERSONA_KEYS = ['rile', 'maya', 'elena'];

// Mapping from persona KEY (URL-stable, used internally) to the persona
// ID used in `Ecosystem.assistantPersonas`. The agent and the website
// share these IDs: keep them in sync with rile-connect-agent's
// TOOL_REGISTRY keys. Wave M1 / Agent Wave C settled on lower-case
// human-readable IDs (`aspen` not `rile`) for the per-tenant override
// keys; the website's internal key for the same persona is still `rile`
// for backward compatibility with existing URLs.
const PERSONA_OVERRIDE_KEY = {
    rile: 'aspen',
    maya: 'maya',
    elena: 'elena',
};

/**
 * Build the default persona dictionary templated on the active ecosystem
 * name. The returned object is the website's source of truth for persona
 * copy WHEN the tenant hasn't authored an override for that persona /
 * field. Per-field overrides from `ecosystem.assistantPersonas` are
 * applied on top by `buildPersonas` below.
 *
 * @param {string} ecoName - The ecosystem name (or a fallback like
 *   "this ecosystem" / "RILE Connect" for platform mode).
 */
const buildDefaultPersonas = (ecoName) => ({
    rile: {
        name: 'Aspen',
        description: 'General ecosystem assistant',
        image: '/images/personas/aspen.png',
        greeting:
            `Hello! I'm Aspen, your guide to ${ecoName}. ` +
            'I can help you find organizations, mentors, and opportunities. What are you looking for?',
        suggestedQuestions: [
            'What organizations are in your area?',
            'Find mentors with data science expertise',
            'What STEM programs are available for students?',
            'Show me nonprofits that offer internships',
        ],
        about: `Aspen is a general-purpose assistant that helps anyone explore ${ecoName}.`,
    },
    maya: {
        name: 'Maya',
        description: 'Student mentor matching',
        image: '/images/personas/maya.png',
        greeting:
            `Hi! I'm Maya, your guide to finding mentorship and career opportunities in ${ecoName}. ` +
            'I can help you discover organizations, connect with mentors, and explore STEM pathways. What are you looking for?',
        suggestedQuestions: [
            'Find internship opportunities near me',
            'I need a mentor in data science',
            'What organizations offer summer programs?',
            'Show me nonprofits that work with students',
        ],
        about: 'Maya helps postsecondary students find mentorship, internships, and career pathways in STEM.',
    },
    elena: {
        name: 'Elena',
        description: 'Educator professional development',
        image: '/images/personas/elena.png',
        greeting:
            "Hello! I'm Elena, here to help K-12 STEM educators find professional development and resources. " +
            'I can connect you with organizations offering teacher PD, curriculum support, and peer collaboration. How can I help?',
        suggestedQuestions: [
            'Find teacher professional development',
            'What organizations offer STEM curriculum?',
            'I teach in a rural district, what resources are available?',
            'Show me after-school program partners',
        ],
        about: 'Elena helps K-12 educators find professional development, curriculum resources, and peer support.',
    },
});

/**
 * Type-guarded reader for a single override field. The `assistantPersonas`
 * schema is `Schema.Types.Mixed` (Mongo lets a tenant author any object
 * shape there), so the website can NEVER assume the field is the right
 * type. Strings must be non-empty strings; suggestedQuestions must be a
 * non-empty array of non-empty strings. Anything else falls through to
 * the default (silently) because surfacing a tenant-authoring error
 * inside the chat UI is the wrong place for it.
 *
 * @param {*} override - Raw override value from `assistantPersonas[key]`.
 * @param {string} field - Field name being read (greeting / about /
 *   description / suggestedQuestions).
 * @returns {*|null} The override value if it passes the type check,
 *   else `null` (caller falls through to default).
 */
function readPersonaField(override, field) {
    if (!override || typeof override !== 'object') return null;
    const value = override[field];
    if (value === undefined || value === null) return null;
    if (field === 'suggestedQuestions') {
        if (!Array.isArray(value) || value.length === 0) return null;
        const cleaned = value.filter(
            (q) => typeof q === 'string' && q.trim().length > 0
        );
        return cleaned.length > 0 ? cleaned : null;
    }
    // Scalar string fields: name, description, greeting, about, image.
    if (typeof value !== 'string') return null;
    if (value.trim().length === 0) return null;
    return value;
}

/**
 * Build the final persona dictionary by layering the tenant's
 * `assistantPersonas` overrides (if any) on top of the code-side
 * defaults. Override granularity is per-field: a tenant may override
 * just the greeting on Maya without touching her suggestedQuestions or
 * about copy.
 *
 * Mixed-type safety: every override read goes through `readPersonaField`
 * which type-checks the value and falls through silently on a mismatch.
 *
 * @param {object|null} ecosystem - The active ecosystem record (or null
 *   for platform mode).
 * @returns {object} Map of persona-key → persona content.
 */
export const buildPersonas = (ecosystem) => {
    const ecoName = (ecosystem && ecosystem.name) || 'this ecosystem';
    const defaults = buildDefaultPersonas(ecoName);
    const overrides = (ecosystem && ecosystem.assistantPersonas) || {};

    const result = {};
    for (const key of PERSONA_KEYS) {
        const overrideKey = PERSONA_OVERRIDE_KEY[key];
        const personaOverride = overrides[overrideKey];
        const base = defaults[key];
        result[key] = {
            name: readPersonaField(personaOverride, 'name') || base.name,
            description:
                readPersonaField(personaOverride, 'description') ||
                base.description,
            image: readPersonaField(personaOverride, 'image') || base.image,
            greeting:
                readPersonaField(personaOverride, 'greeting') || base.greeting,
            suggestedQuestions:
                readPersonaField(personaOverride, 'suggestedQuestions') ||
                base.suggestedQuestions,
            about: readPersonaField(personaOverride, 'about') || base.about,
        };
    }
    return result;
};

export default function Assistant() {
    // Get persona from URL params (e.g., /assistant/maya or /assistant/elena)
    const { persona: urlPersona } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { ecosystem } = useEcosystem();

    // Templated per-tenant copy. Rebuilt when the ecosystem name OR the
    // per-tenant persona overrides change. `assistantPersonas` is a Mixed
    // field: using it directly in the dep array compares by reference,
    // which is fine because the EcosystemContext only re-resolves on a
    // page boot and the record is stable within a session.
    const PERSONAS = React.useMemo(() => buildPersonas(ecosystem), [ecosystem]);

    // Determine initial persona from URL or default to 'rile'
    const getInitialPersona = () => {
        if (urlPersona && PERSONA_KEYS.includes(urlPersona)) {
            return urlPersona;
        }
        return 'rile';
    };

    // State
    const [persona, setPersona] = useState(getInitialPersona);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [connecting, setConnecting] = useState(true);
    const [error, setError] = useState(null);
    const [errorType, setErrorType] = useState(null);
    const [usageStats, setUsageStats] = useState(null);
    const [activeThreadId, setActiveThreadId] = useState(null);

    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const currentPersona = PERSONAS[persona];

    // Conversation history
    const { conversations, loadingHistory, refresh, remove } =
        useConversationHistory();

    // Scroll to bottom of messages
    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    // Initialize greeting message for current persona
    const initializeGreeting = useCallback(() => {
        setMessages([
            {
                id: 'greeting',
                text: currentPersona.greeting,
                sender: 'bot',
                timestamp: new Date(),
            },
        ]);
    }, [currentPersona.greeting]);

    // Connect to agent on mount and persona change
    useEffect(() => {
        const threadFromUrl = searchParams.get('thread');

        const connectToPersona = async () => {
            setConnecting(true);
            setError(null);
            setErrorType(null);

            try {
                // If a thread ID was passed (e.g., from "Continue in Assistant"),
                // connect to that specific thread and load its messages
                if (threadFromUrl) {
                    await connectToAgent(threadFromUrl, persona);
                    setActiveThreadId(threadFromUrl);

                    // Load existing messages from the thread
                    try {
                        const conv =
                            await getConversationFromAgent(threadFromUrl);
                        if (conv.messages && conv.messages.length > 0) {
                            setMessages(conv.messages);
                        } else {
                            initializeGreeting();
                        }
                    } catch {
                        // If loading fails, just show greeting
                        initializeGreeting();
                    }

                    // Clear the query param so it doesn't persist on refresh
                    navigate(`/assistant/${persona}`, { replace: true });
                } else {
                    // Normal connection: new conversation
                    await connectToAgent(null, persona);
                    initializeGreeting();
                }

                // Load usage stats and conversation history
                const stats = await getAgentStats();
                setUsageStats(stats);
                refresh();
            } catch (err) {
                console.error('Failed to connect to agent:', err);
                const wsErrorType = getAgentErrorType();

                if (wsErrorType === WS_ERROR_TYPES.PERMISSION_DENIED) {
                    setErrorType('permission_denied');
                    setError(
                        'You do not have permission to access the Assistant. Please contact an administrator.'
                    );
                } else if (wsErrorType === WS_ERROR_TYPES.AUTH_ERROR) {
                    setErrorType('auth_error');
                    setError('Authentication failed. Please log in again.');
                } else {
                    setErrorType('connection_error');
                    setError(
                        'Unable to connect to the Assistant. Please try again later.'
                    );
                }
            } finally {
                setConnecting(false);
            }
        };

        connectToPersona();

        // Cleanup on unmount
        return () => {
            disconnectFromAgent();
        };
    }, [persona, initializeGreeting]); // eslint-disable-line

    // Handle sending a message
    const handleSend = async (e) => {
        e.preventDefault();
        if (!input.trim() || loading || !isConnectedToAgent()) return;

        const userMessage = {
            id: `user-${Date.now()}`,
            text: input,
            sender: 'user',
            timestamp: new Date(),
        };

        setMessages((prev) => [...prev, userMessage]);
        setInput('');
        setLoading(true);

        try {
            const response = await sendMessageToAgent(input);

            const botMessage = {
                id: response.id || `bot-${Date.now()}`,
                text: response.text,
                sender: 'bot',
                timestamp: response.timestamp || new Date(),
                thinking: response.thinking,
            };

            setMessages((prev) => [...prev, botMessage]);

            // Capture thread ID from server response and refresh stats/history
            const tid = response.threadId || getCurrentThreadId();
            if (tid) setActiveThreadId(tid);
            const stats = await getAgentStats();
            setUsageStats(stats);
            refresh();
        } catch (err) {
            console.error('Failed to send message:', err);

            // Check if it's a rate limit error
            const errorMessage = err.message || 'Failed to get a response';
            const botError = {
                id: `error-${Date.now()}`,
                text: `I'm sorry, I encountered an error: ${errorMessage}. Please try again.`,
                sender: 'bot',
                timestamp: new Date(),
                isError: true,
            };

            setMessages((prev) => [...prev, botError]);
        } finally {
            setLoading(false);
            inputRef.current?.focus();
        }
    };

    // Handle persona switch
    const handlePersonaSwitch = (newPersona) => {
        if (newPersona !== persona) {
            setPersona(newPersona);
            setMessages([]);
            // Update URL to reflect persona change
            navigate(`/assistant/${newPersona}`, { replace: true });
        }
    };

    // Handle suggested question click
    const handleSuggestedQuestion = (question) => {
        setInput(question);
        inputRef.current?.focus();
    };

    // Retry connection
    const handleRetry = () => {
        setError(null);
        setErrorType(null);
        setConnecting(true);
        // Trigger reconnect by toggling persona state
        setPersona((p) => p);
    };

    // Switch to an existing conversation
    const handleSelectConversation = async (threadId) => {
        if (threadId === activeThreadId) return;
        setLoading(true);
        try {
            await switchAgentConversation(threadId);
            setActiveThreadId(threadId);

            const conv = await getConversationFromAgent(threadId);
            if (conv.messages && conv.messages.length > 0) {
                setMessages(conv.messages);
            } else {
                initializeGreeting();
            }
        } catch (err) {
            console.error('Failed to switch conversation:', err);
        } finally {
            setLoading(false);
        }
    };

    // Start a new conversation
    const handleNewChat = async () => {
        setLoading(true);
        try {
            const result = await createNewAgentConversation();
            setActiveThreadId(result.threadId);
            initializeGreeting();
            refresh();
        } catch (err) {
            console.error('Failed to create new conversation:', err);
        } finally {
            setLoading(false);
        }
    };

    // Delete a conversation from history
    const handleDeleteConversation = async (threadId) => {
        try {
            await remove(threadId);
            // If we deleted the active conversation, start a new one
            if (threadId === activeThreadId) {
                await handleNewChat();
            }
        } catch (err) {
            console.error('Failed to delete conversation:', err);
        }
    };

    // Render error state
    if (errorType === 'permission_denied') {
        return (
            <>
                <Helmet>
                    <title>Access Denied | RILE Connect</title>
                </Helmet>
                <Container className="py-5">
                    <Row className="justify-content-center">
                        <Col md={6}>
                            <Alert variant="danger" className="text-center">
                                <Alert.Heading>Access Denied</Alert.Heading>
                                <p>{error}</p>
                            </Alert>
                        </Col>
                    </Row>
                </Container>
            </>
        );
    }

    if (errorType === 'connection_error' || errorType === 'auth_error') {
        return (
            <>
                <Helmet>
                    <title>Connection Error | RILE Connect</title>
                </Helmet>
                <Container className="py-5">
                    <Row className="justify-content-center">
                        <Col md={6}>
                            <Alert variant="warning" className="text-center">
                                <Alert.Heading>Connection Error</Alert.Heading>
                                <p>{error}</p>
                                <Button variant="primary" onClick={handleRetry}>
                                    Try Again
                                </Button>
                            </Alert>
                        </Col>
                    </Row>
                </Container>
            </>
        );
    }

    // Render loading state
    if (connecting) {
        return (
            <>
                <Helmet>
                    <title>Connecting... | RILE Connect</title>
                </Helmet>
                <Container className="py-5">
                    <Row className="justify-content-center">
                        <Col md={6} className="text-center">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">
                                Connecting to {currentPersona.name}...
                            </p>
                        </Col>
                    </Row>
                </Container>
            </>
        );
    }

    return (
        <>
            <Helmet>
                <title>{currentPersona.name} - Assistant | RILE Connect</title>
            </Helmet>
            <Container fluid className="assistant-container">
                <Row className="h-100">
                    {/* Chat Area */}
                    <Col lg={9} className="d-flex flex-column p-0">
                        {/* Header with persona selector */}
                        <div className="chat-header bg-primary text-white p-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h4 className="mb-0 d-flex align-items-center gap-2">
                                        <img
                                            src={currentPersona.image}
                                            alt={currentPersona.name}
                                            className="persona-avatar"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                        {currentPersona.name}
                                    </h4>
                                    <p className="mb-0 small opacity-75">
                                        {currentPersona.description}
                                    </p>
                                </div>
                                <div className="persona-switcher">
                                    <Button
                                        variant={
                                            persona === 'rile'
                                                ? 'light'
                                                : 'outline-light'
                                        }
                                        size="sm"
                                        onClick={() =>
                                            handlePersonaSwitch('rile')
                                        }
                                        className="me-2"
                                    >
                                        Aspen
                                    </Button>
                                    <Button
                                        variant={
                                            persona === 'maya'
                                                ? 'light'
                                                : 'outline-light'
                                        }
                                        size="sm"
                                        onClick={() =>
                                            handlePersonaSwitch('maya')
                                        }
                                        className="me-2"
                                    >
                                        Maya
                                    </Button>
                                    <Button
                                        variant={
                                            persona === 'elena'
                                                ? 'light'
                                                : 'outline-light'
                                        }
                                        size="sm"
                                        onClick={() =>
                                            handlePersonaSwitch('elena')
                                        }
                                    >
                                        Elena
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {/* Messages */}
                        <div className="chat-messages flex-grow-1 p-4">
                            <ChatMessages
                                messages={messages}
                                loading={loading}
                                loadingLabel={`${currentPersona.name} is thinking...`}
                                messagesEndRef={messagesEndRef}
                            />
                        </div>

                        {/* Input */}
                        <div className="chat-input bg-light p-3 border-top">
                            <ChatInput
                                input={input}
                                onInputChange={setInput}
                                onSend={handleSend}
                                loading={loading}
                                placeholder={`Ask ${currentPersona.name} about organizations, programs, or mentorship...`}
                                inputRef={inputRef}
                            />
                        </div>
                    </Col>

                    {/* Sidebar */}
                    <Col
                        lg={3}
                        className="sidebar bg-white border-start p-4 d-none d-lg-block"
                    >
                        {/* Conversation History */}
                        <div className="mb-4">
                            <ConversationHistory
                                conversations={conversations}
                                activeThreadId={activeThreadId}
                                onSelect={handleSelectConversation}
                                onDelete={handleDeleteConversation}
                                onNewChat={handleNewChat}
                                loading={loadingHistory}
                            />
                        </div>

                        <hr className="my-3" />

                        {/* Usage Stats */}
                        {usageStats && (
                            <div className="mb-4">
                                <h6 className="text-muted mb-2">Daily Usage</h6>
                                <ProgressBar
                                    now={
                                        usageStats.dailyLimit > 0
                                            ? (usageStats.dailyMessages /
                                                  usageStats.dailyLimit) *
                                              100
                                            : 0
                                    }
                                    variant={
                                        usageStats.dailyMessages >
                                        usageStats.dailyLimit * 0.8
                                            ? 'warning'
                                            : 'primary'
                                    }
                                    className="mb-1"
                                />
                                <small className="text-muted">
                                    {usageStats.dailyMessages} /{' '}
                                    {usageStats.dailyLimit > 0
                                        ? usageStats.dailyLimit
                                        : '∞'}{' '}
                                    messages today
                                </small>
                            </div>
                        )}

                        <h5 className="text-primary mb-3">
                            Suggested Questions
                        </h5>
                        <div className="d-flex flex-column gap-2">
                            {currentPersona.suggestedQuestions.map(
                                (question, index) => (
                                    <Button
                                        key={index}
                                        variant="outline-secondary"
                                        size="sm"
                                        className="text-start"
                                        onClick={() =>
                                            handleSuggestedQuestion(question)
                                        }
                                    >
                                        {question}
                                    </Button>
                                )
                            )}
                        </div>

                        <hr className="my-4" />

                        <h5 className="text-primary mb-3">Quick Actions</h5>
                        <div className="d-flex flex-column gap-2">
                            <Button
                                as={Link}
                                to="/directory"
                                variant="outline-primary"
                                size="sm"
                            >
                                Browse Ecosystem
                            </Button>
                            <Button
                                as={Link}
                                to="/organizations/onboard"
                                variant="outline-primary"
                                size="sm"
                            >
                                Register Organization
                            </Button>
                        </div>

                        <hr className="my-4" />

                        {/* Persona Info */}
                        <div className="persona-info">
                            <h6 className="text-muted mb-2">
                                About {currentPersona.name}
                            </h6>
                            <p className="small text-muted mb-0">
                                {currentPersona.about}
                            </p>
                        </div>
                    </Col>
                </Row>
            </Container>
        </>
    );
}
